import React, { useState } from "react";
import { auth, googleProvider, isFirebaseConfigured } from "../firebase-config";
import { createUserWithEmailAndPassword, signInWithPopup } from "firebase/auth";

function SignUp() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleSignUp = async () => {
    if (!isFirebaseConfigured() || !auth || !googleProvider) {
      alert("Google sign-up is not configured");
      return;
    }

    setLoading(true);
    try {
      // Sign up with Google popup
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();

      // Send token to backend (backend will create user if needed)
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
        credentials: "include",
      });

      const data = await response.json();

      if (data.error) {
        console.error("Error with Google sign-up:", data.error);
        // Show a user-friendly message
        if (data.error.includes("already associated with an email/password account")) {
          alert("This Google account email is already registered. Please go to the sign-in page and use your email and password.");
        } else {
          alert(data.error);
        }
      } else {
        console.log("Google sign-up successful");
        window.location.href = `${process.env.REACT_APP_CLIENT_URL}/recipes`;
      }
    } catch (error) {
      console.error("Error signing up with Google:", error);
      alert(error.message || "Error signing up with Google");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username || !email || !password || !confirmPassword) {
      alert("All fields are required");
      return;
    }
    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      // If Firebase is configured, create Firebase user first
      if (isFirebaseConfigured() && auth) {
        try {
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const firebaseUid = userCredential.user.uid;

          // Send Firebase UID to backend to create MySQL user
          const response = await fetch(`${process.env.REACT_APP_API_URL}/signup/firebase`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, email, firebaseUid }),
            credentials: "include",
          });

          const data = await response.json();

          if (data.error) {
            console.error("Error creating user in database:", data.error);
            alert(data.error);
          } else {
            console.log("Firebase user created successfully");
            // User is already authenticated with Firebase, redirect to app
            window.location.href = `${process.env.REACT_APP_CLIENT_URL}/recipes`;
          }
          return;
        } catch (firebaseError) {
          console.error("Firebase signup error:", firebaseError);
          alert(firebaseError.message || "Error creating Firebase account");
          setLoading(false);
          return;
        }
      }

      // Fallback to legacy signup if Firebase not configured
      const response = await fetch(`${process.env.REACT_APP_API_URL}/signup/free`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, email, password }),
      });

      console.log("Server response:", response);
      if (response.ok) {
        console.log("User signed up successfully");
        window.location.href = "/signin";
      } else {
        const data = await response.json();
        const errorElement = document.getElementById("error-message");
        if (errorElement) {
          errorElement.textContent = data.error;
        }
      }
    } catch (error) {
      console.error("Error signing up:", error);
      alert("An error occurred during sign up");
    } finally {
      setLoading(false);
    }
  };

return (
  <div className="container mx-auto py-4" style={{ maxWidth: "450px" }}>
    <div className="shadow-lg rounded p-4" style={{backgroundColor: "white"}}>
      <h2 className="text-center mb-4">Sign Up</h2>

      <form onSubmit={handleSubmit}>
        <div className="form-group mb-3">
          <label htmlFor="username" className="form-label">Username</label>
          <input
            type="text"
            className="form-control"
            id="username"
            name="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="Choose a username"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group mb-3">
          <label htmlFor="email" className="form-label">Email</label>
          <input
            type="email"
            className="form-control"
            id="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="Enter your email"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group mb-3">
          <label htmlFor="password" className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            id="password"
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Create a password (min 6 characters)"
            required
            disabled={loading}
          />
        </div>

        <div className="form-group mb-3">
          <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
          <input
            type="password"
            className="form-control"
            id="confirmPassword"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder="Confirm your password"
            required
            disabled={loading}
          />
        </div>

        <p style={{ color: "red", fontSize: "0.9rem" }} id="error-message"></p>

        <button type="submit" className="btn btn-primary w-100 mb-3" disabled={loading}>
          {loading ? "Signing up..." : "Sign Up"}
        </button>

        {/* Google Sign-Up Button */}
        {isFirebaseConfigured() && (
          <>
            <div className="text-center mb-2" style={{position: "relative"}}>
              <span style={{
                backgroundColor: "white",
                padding: "0 10px",
                color: "#666",
                fontSize: "0.9rem",
                position: "relative",
                zIndex: 1
              }}>or</span>
              <div style={{
                position: "absolute",
                top: "50%",
                left: 0,
                right: 0,
                height: "1px",
                backgroundColor: "#ddd",
                zIndex: 0
              }}></div>
            </div>

            <button
              type="button"
              className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
              onClick={handleGoogleSignUp}
              disabled={loading}
              style={{ gap: "10px" }}
            >
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              {loading ? "Signing up..." : "Sign up with Google"}
            </button>
          </>
        )}
      </form>

      <div className="text-center mt-4" style={{fontSize: "0.9rem"}}>
        Already have an account? <a href="/signin">Sign in</a>
      </div>
    </div>
  </div>
);

}

export default SignUp;
